// East Carolina University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - ECU's Common Data Set 2024-2025 (CDS_2024-2025_Redacted.pdf), the
//    latest edition the Office of Institutional Planning, Assessment and
//    Research links from its Publicly Available ECU Data page (the SOURCES
//    url is that ecu.edu page). No 2025-2026 edition is posted, so the counts
//    are for the Fall 2024 entering class. C1 applicants, admits and
//    enrollees with the residency table; C21/C22 plans. Its C8 covers fall
//    2026 applicants, so the fall 2027 test policy comes from Admissions.
//    The IPAR site answers curl with 403; the page was read with WebFetch and
//    the PDF itself downloaded directly.
//  - ECU Undergraduate Admissions, Freshmen (dates and deadlines for the
//    application cycle that runs November 1, 2026 to April 1, 2027, the
//    required materials, test scores and the Honors College). Quirk: the
//    test paragraph sits under "Updates for Fall 2026" and names the Fall
//    2026 and Spring 2027 terms; the same rule is listed among the required
//    application materials for this cycle, which is what the page uses.
//    ECU admits on a rolling basis and publishes no decision dates.
//  - ECU Office of Student Financial Aid, 2026-27 Undergraduate Estimated
//    Cost of Attendance, entering students living on campus. The site blocks
//    curl (AWS WAF challenge), so it was read with WebFetch; each column's
//    items add up to its stated total. Health insurance is listed at $0 and
//    left out.
//  - College Scorecard, school id 198464 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'ECU Common Data Set 2024-2025',
        url: 'https://ipar.ecu.edu/research/rsrch-public-ecu-data/',
        read: '2026-10-01',
    },
    freshmen: {
        label: 'ECU Undergraduate Admissions, Freshmen',
        url: 'https://admissions.ecu.edu/apply/freshmen/',
        read: '2026-10-01',
    },
    costs: {
        label: 'ECU Office of Student Financial Aid, Estimated Cost of Attendance',
        url: 'https://financialaid.ecu.edu/estimated-cost-of-attendance-coa/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?198464-East-Carolina-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'east-carolina-university',
    kbId: 'east_carolina_university',
    name: 'East Carolina University',
    shortName: 'East Carolina',
    aliases: ['ECU'],
    location: 'Greenville, NC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (ECU brand guide, Our Brand); decoration only.
    branding: {
        primary: '#592A8A',
        secondary: '#FEC923',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/east_carolina_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 26369,
        admitted: 23527,
        enrolled: 4022,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 17413, admitted: 16367 },
            { group: 'Out-of-state', applied: 8643, admitted: 6867 },
            { group: 'International', applied: 313, admitted: 292 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'ECU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'ECU has no early decision or early action plan. It admits on a rolling basis, and its November 1 priority deadline is an earlier filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to ECU as a freshman. One program has its own step:',
        rows: [
            { school: 'Honors College', requirement: 'Submit your completed ECU application by November 1, 2026, with transcripts and any test scores received by then. Eligible students are invited to complete the Honors College application by December 1, 2026.' },
        ],
        source: 'freshmen',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final priority deadline', value: 'April 1, 2027' },
        rows: [
            { plan: 'Priority freshman deadline', deadline: 'November 1, 2026', decision: null, binding: false },
            { plan: 'Merit scholarship eligibility deadline', deadline: 'December 15, 2026', decision: null, binding: false },
            { plan: 'Final freshman priority deadline', deadline: 'April 1, 2027', decision: null, binding: false },
        ],
        note: 'ECU offers rolling admission: once your application is complete, it is reviewed and a decision is sent, so ECU encourages early applications and publishes no decision dates. You can apply through the ECU Application, CFNC or the Common App; the application fee is $75, nonrefundable.',
        source: 'freshmen',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test-optional for most applicants, but not all: students with a weighted high school GPA below 2.8 must submit an ACT or SAT score. With a weighted GPA of 2.8 or higher, a test score is not required.',
        source: 'freshmen',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2026 applicants, for the same reason: a weighted GPA below 2.8.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshmen',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (including the $185 orientation fee)', amounts: [7789, 24555] },
            { item: 'Food', amounts: [5521, 5521] },
            { item: 'Housing', amounts: [6699, 6699] },
            { item: 'Books and supplies', amounts: [1570, 1570] },
            { item: 'Personal expenses', amounts: [2067, 2067] },
            { item: 'Transportation', amounts: [2380, 2380] },
            { item: 'Loan fee', amounts: [58, 58] },
        ],
        totals: [26084, 42850],
        note: 'ECU\'s estimate for an entering undergraduate living on campus.',
        source: 'costs',
        netPrice: {
            average: 16880,
            byIncome: [
                { income: '$0 to $30,000', amount: 12125 },
                { income: '$30,001 to $48,000', amount: 12558 },
                { income: '$48,001 to $75,000', amount: 14074 },
                { income: '$75,001 to $110,000', amount: 19510 },
                { income: 'Over $110,000', amount: 24257 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from ECU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
